export interface AuthUser {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
}

export interface AuthOrganization {
  id: string
  externalId?: string | null
  name: string
  role: 'admin' | 'member'
}

export interface AuthWorkspace {
  id: string
  key: string
  name: string
  status: string
}

export interface AuthSession {
  authenticated: boolean
  user: AuthUser | null
  localUserId: string | null
  organization: AuthOrganization | null
  organizations: AuthOrganization[]
  workspaces: AuthWorkspace[]
  activeWorkspace: AuthWorkspace | null
}

let cachedSession: AuthSession | null = null
let lastCheck = 0
let pendingPromise: Promise<AuthSession> | null = null
let signInStarted = false
const CACHE_TTL = 30_000
export const AUTH_SESSION_REFRESH_EVENT = 'golf:auth-session-refresh'

export class AuthSessionError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'AuthSessionError'
    this.status = status
  }
}

async function fetchSession(): Promise<AuthSession> {
  const res = await fetch('/auth/session', { credentials: 'include' })
  if (!res.ok) {
    const body: { error?: string } = await res.json().catch(() => ({}))
    throw new AuthSessionError(res.status, body.error || `Session check failed (${res.status})`)
  }
  return normalizeSession(await res.json())
}

function normalizeSession(raw: Partial<AuthSession>): AuthSession {
  return {
    authenticated: Boolean(raw.authenticated),
    user: raw.user ?? null,
    localUserId: raw.localUserId ?? null,
    organization: raw.organization ?? null,
    organizations: raw.organizations ?? [],
    workspaces: raw.workspaces ?? [],
    activeWorkspace: raw.activeWorkspace ?? null,
  }
}

export const auth = {
  getSession: async (): Promise<AuthSession> => {
    const now = Date.now()
    if (cachedSession && now - lastCheck < CACHE_TTL) return cachedSession
    if (pendingPromise) return pendingPromise

    pendingPromise = fetchSession()
    try {
      const session = await pendingPromise
      cachedSession = session
      lastCheck = now
      return session
    } finally {
      pendingPromise = null
    }
  },

  clearCache: () => {
    cachedSession = null
    lastCheck = 0
    pendingPromise = null
    signInStarted = false
  },

  refreshSession: () => {
    auth.clearCache()
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_SESSION_REFRESH_EVENT))
    }
  },

  signIn: (organizationId?: string | null) => {
    if (signInStarted) return
    signInStarted = true
    const qs = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ''
    window.location.assign(`/auth/login${qs}`)
  },

  switchOrganization: (workOSOrganizationId: string) => {
    auth.clearCache()
    auth.signIn(workOSOrganizationId)
  },

  signOut: async () => {
    const res = await fetch('/auth/logout', {
      method: 'POST',
      credentials: 'include',
    })
    auth.clearCache()
    const data: { logoutUrl?: string } = await res.json().catch(() => ({}))
    window.location.href = data.logoutUrl || '/auth/login'
  },
}
